import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Spinner,
  TextButton,
} from '@zillow/constellation-10';
import { IconHouseFilled } from '@zillow/constellation-icons';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { getListingAliasFromPath } from '@zg-rentals/string-utils';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserTrackingV2WebApiHandler_event,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useConfirmProperty, useContracts, useValidateListing } from '../../../hooks';
import accessUtils from '../../../utils/accessUtils';
import { setIsNewListing } from '../../../utils/navigationCacheUtils';
import { isRedirectToPayments } from '../../../utils/propertiesPageUtils';
import propertyUtils from '../../../utils/propertyUtils';
import { validateNumUnits } from '../../../utils/propertyValidationUtils';
import { useSavePropertyInfo } from './hooks';
import { listingErrors as listingErrorsReducer, listing as listingReducer } from './reducers';
import { shouldDisplayRoomForRentCheckbox } from './utils';

import { UNIT_ALERT, type UnitAlertValue } from '../../../constants/AddPropertyConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import { EXTERNAL_ROUTES, getCreateListingRoute, getListingRoute } from '../../../constants/urls';
import { BEHAVIOR_OPTIONS, EMPTY_ERROR, EMPTY_LISTING, STEPS } from './constants';

import {
  EVENT_IDS as ADD_PROPERTY_EVENT_IDS,
  emitCreateListingEvent,
  emitGoBackAndEditAddressEvent,
  emitSeeAllActionsEvent,
} from '../../../analytics/events/addPropertyModal';
import AddPropertyForm from './components/AddPropertyForm';
import ConfirmAddress from './components/ConfirmAddress';
import DeleteButton from './components/DeleteButton';
import MultiplexUnitEntry from './components/MultiplexUnitEntry';
import MultiplexUnitsAdded from './components/MultiplexUnitsAdded';
import SelectAvailableActions from './components/SelectAvailableActions';

import {
  emitMultiplexAddListingDetailsClickEvent,
  emitMultiplexConfirmAddressClickEvent,
  emitMultiplexConfirmUnitEntryClickEvent,
  EVENT_IDS as MULTIPLEX_EVENT_IDS,
} from '../../../analytics/events/multiplex';
import { useAuth } from '../../AuthProvider';
import ListingActivationTerms from '../../ListingActivationTerms';

import type { ValueOf } from 'type-fest';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type { UserPropertyMultiplexCreateUnitResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { ListingReducerProps } from './reducers';
import type { MultiplexUnitEntryRef } from './components/MultiplexUnitEntry';
import type { MultiplexUnitsAddedRef } from './components/MultiplexUnitsAdded';

const {
  LISTING_CODES_BY_TYPE,
  PROPERTY_TYPES,
  PROPERTY_TYPES: { COMMUNITY, MULTIPLEX },
  PROPERTY_TYPES_BY_CODE,
  LISTING_TYPES: { ROOM },
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const getErrorMessage = (err: { message?: unknown }) => {
  const msg = err?.message;
  if (typeof msg !== 'string' || msg.length > 300 || /^[{[]/.test(msg)) {
    return GENERIC_ERROR_MESSAGE;
  }
  return msg;
};

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  behavior?: ValueOf<typeof BEHAVIOR_OPTIONS>;
};

const AddPropertyModal = ({
  isOpen = false,
  onClose = () => {},
  behavior = BEHAVIOR_OPTIONS.ADD,
}: Props): JSX.Element => {
  const { user } = useAuth();
  const router = useRouter();

  const multiplexUnitEntryRef = useRef<MultiplexUnitEntryRef>(null);
  const multiplexUnitsAddedRef = useRef<MultiplexUnitsAddedRef>(null);
  const [isMultiplexProcessing, setIsMultiplexProcessing] = useState(false);

  const shouldRedirectToPayments = isRedirectToPayments(router.query);

  const queryClient = useQueryClient();
  const { savePropertyInfo } = useSavePropertyInfo();
  const { mutate: trackEvent } = useRentalManagerUserTrackingV2WebApiHandler_event({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'Tracking user event',
      },
    },
  });

  const [step, setStep] = useState<ValueOf<typeof STEPS>>(STEPS.INPUT_ADDRESS);
  const [isSelectActionFinalStep, setIsSelectActionFinalStep] = useState(false);
  const [unitAlert, setUnitAlert] = useState<UnitAlertValue | null>(null);
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<JSX.Element | string>
  >('');
  const [isRefocusingAddressInput, setIsRefocusingAddressInput] = useState(false);
  const [unitsCreated, setUnitsCreated] = useState<Array<UserPropertyMultiplexCreateUnitResponseWebDto>>([]);
  const [hasPartialUnitsSucceeded, setHasPartialUnitsSucceeded] = useState<boolean>(false);

  // Get listingAlias from path for edit mode
  const listingAliasFromQuery = typeof window !== 'undefined' ? getListingAliasFromPath(window.location.pathname) : '';

  const { data: propertyDetailsData } = useRentalManagerUserPropertyDetailsWebApiHandler_details(
    {
      listingAlias: listingAliasFromQuery,
    },
    {
      enabled: Boolean(listingAliasFromQuery) && behavior === BEHAVIOR_OPTIONS.EDIT,
    },
  );

  const getInitialListing = () => {
    const urlContainsAddressParam = 'address' in router.query;

    return !urlContainsAddressParam
      ? EMPTY_LISTING
      : {
          ...EMPTY_LISTING,
          address: typeof router.query.address === 'string' ? router.query.address.replace(/-/g, ' ') : undefined,
          unit: typeof router.query.unit === 'string' ? router.query.unit : undefined,
        };
  };

  const initialListing = getInitialListing();

  const [listing, setListing] = useReducer(listingReducer, initialListing);
  const [errorObj, setErrorObj] = useReducer(listingErrorsReducer, EMPTY_ERROR);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState(false);
  const [isImpreciseLocation, setIsImpreciseLocation] = useState(false);

  const canAccessMultiplexListingFlowResponse = getBooleanConfig('canAccessMultiplexListingFlow', true);

  const isMultiplexType = listing?.propertyType === MULTIPLEX;
  const isPromptUnitNumberCheckRequired = isMultiplexType ? false : unitAlert !== UNIT_ALERT.PROMPT_FOR_UNIT;

  const contracts = useContracts(setListing, setErrorObj, setIsSubmitDisabled, user ?? undefined);

  const { verifyCanSubmitForm, handleCheckLocationRestriction } = useValidateListing(
    listing,
    setListing,
    errorObj,
    setErrorObj,
    setUnitAlert,
    setPropertyAlreadyPostedWarningMessage,
    setIsImpreciseLocation,
    user ?? undefined,
    contracts,
  );

  const canAccessMultiplexListingFlow = accessUtils.canAccessMultiplexListingFlow({
    canAccessMultiplexListingFlowResponse,
  });

  const { confirmProperty } = useConfirmProperty({
    user: user ?? undefined,
    isSelectActionFinalStep,
    onListingAliasSet: (alias) => setListing({ type: 'listingAlias', payload: alias }),
  });

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        postData: {
          trackingEvent: RentalManagerUserTrackingEvent.OPEN_ADD_PROPERTY_MODAL,
        },
      });
    } else {
      setIsMultiplexProcessing(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isOpen]);

  useEffect(() => {
    if (listingAliasFromQuery && propertyDetailsData && behavior === BEHAVIOR_OPTIONS.EDIT) {
      setListing({
        type: 'UPDATE_ALL',
        payload: {
          address: propertyUtils.formatAddress({
            street: propertyDetailsData.street,
            city: propertyDetailsData.city,
            state: propertyDetailsData.state,
            zip: propertyDetailsData.zip,
            unit: propertyDetailsData.unit,
          }).formattedAddressWithoutUnit,
          isRoomForRent: propertyDetailsData.listingType.code === Number(LISTING_CODES_BY_TYPE[ROOM]),
          isMultifamily: propertyDetailsData.isMultifamily,
          numUnits: propertyDetailsData.numUnits != null ? String(propertyDetailsData.numUnits) : undefined,
          customerId: propertyDetailsData.customerId,
          propertyType: propertyDetailsData.propertyType
            ? PROPERTY_TYPES_BY_CODE[propertyDetailsData.propertyType.code]
            : undefined,
          unit: propertyDetailsData.unit != null ? String(propertyDetailsData.unit) : undefined,
          listingAlias: listingAliasFromQuery,
          isAddressEditable: propertyDetailsData.isAddressEditable,
          duplicateAccountListingAlias: null,
        },
      });
    }
  }, [behavior, propertyDetailsData, isOpen, listingAliasFromQuery]);

  const handleListingChange = (keyName: keyof ListingReducerProps, newValue: string | boolean) => {
    const newListing = {
      ...listing,
      [keyName]: newValue,
    };

    setErrorObj({ type: keyName, payload: '' });
    // if the unit was changed, the entire address will be revalidated
    if (keyName === 'unit') {
      setErrorObj({ type: 'address', payload: '' });
    }

    if (newListing.numUnits) {
      const numUnitsError = validateNumUnits(String(newListing.numUnits), newListing.propertyType || '');
      if (numUnitsError) {
        setErrorObj({ type: 'numUnits', payload: numUnitsError });
      }
    }

    setListing({
      type: keyName,
      payload: keyName === 'numUnits' && typeof newValue === 'string' ? newValue.trim() : newValue,
    });

    if (keyName === 'propertyType') {
      setListing({ type: 'isMultifamily', payload: newValue === COMMUNITY });
    }
  };

  const onChangePropertyType = (newPropertyType: string) => handleListingChange('propertyType', newPropertyType);

  const verifyInputAndGoToConfirmSteps = (csEvent: () => void, onValidationSuccess?: () => void) => {
    setIsSubmitDisabled(true);

    verifyCanSubmitForm({ isPromptUnitNumberCheckRequired })
      .then((isValid) => {
        if (isValid) {
          // run any cleanup logic before changing step
          onValidationSuccess?.();
          setStep(STEPS.CONFIRM_ADDRESS);
          csEvent();
        }
      })
      .finally(() => {
        setIsSubmitDisabled(false);
        setIsPrimaryButtonLoading(false);
        setIsSecondaryButtonLoading(false);
      });
  };

  const handleErrorReset = () => {
    setIsSubmitDisabled(false);

    // take user back to add property form to see the error.
    setStep(STEPS.INPUT_ADDRESS);
    setIsPrimaryButtonLoading(false);
    setIsSecondaryButtonLoading(false);
  };

  const editProperty = async () => {
    try {
      await savePropertyInfo(
        {
          fullAddress: listing.address || '',
          unit: !listing.isMultifamily ? listing.unit : undefined,
          numUnits: listing.numUnits,
          propertyId: listingAliasFromQuery || listing.listingAlias || '',
          propertyType: listing.propertyType as ValueOf<typeof PROPERTY_TYPES>,
        },
        {
          onSuccess: () => {
            window.location.reload();
            queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
          },
          onError: (err) => {
            setErrorObj({ type: 'address', payload: getErrorMessage(err) });
            handleErrorReset();
          },
        },
      );
    } catch {
      // Error already handled in onError callback
      logError({ error: 'Failed to edit property:', errorType: 'EditProperty' });
    }
  };

  const handleMultiplexUnityEntryCreateStart = () => {
    setIsMultiplexProcessing(true);
    setIsPrimaryButtonLoading(true);
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntrySuccess = ({
    multiplexBuildingSessionId,
    unitsCreated,
    hasPartialUnitsSucceeded,
  }: {
    multiplexBuildingSessionId: string;
    unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
    hasPartialUnitsSucceeded: boolean;
  }) => {
    setUnitsCreated(unitsCreated);
    setHasPartialUnitsSucceeded(hasPartialUnitsSucceeded);

    if (user) {
      const unitsCreatedList = unitsCreated.map((unit) => unit.listingAlias).join(',');

      const csEventParams = {
        currentProperty: {
          listingAlias: unitsCreatedList,
        },
        user: user ?? {},
      };

      emitMultiplexConfirmUnitEntryClickEvent(csEventParams);
    }

    if (canAccessMultiplexListingFlow) {
      // skip MultiplexUnitsAdded step
      const numUnitsCreated = unitsCreated.length;
      const firstUnitListingAlias = unitsCreated[0].listingAlias;

      if (firstUnitListingAlias) {
        setIsNewListing(true);

        void router.push(
          getCreateListingRoute({
            listingAlias: firstUnitListingAlias,
            multiplexBuildingSessionId: numUnitsCreated > 1 ? multiplexBuildingSessionId : undefined,
          }),
        );
      }
      return;
    }

    setIsMultiplexProcessing(false);
    setStep(STEPS.UNITS_ADDED);

    setIsPrimaryButtonLoading(false);
    // disable submit until a unit is selected
    setIsSubmitDisabled(true);
  };

  const handleMultiplexUnityEntryErrorChange = ({
    hasError,
    isPending,
  }: {
    hasError?: boolean | undefined;
    isPending?: boolean | undefined;
  }) => {
    if (isPending) {
      setIsMultiplexProcessing(true);
      setIsPrimaryButtonLoading(true);
      setIsSubmitDisabled(true);
      return;
    }

    if (hasError) {
      setIsMultiplexProcessing(false);
      setIsPrimaryButtonLoading(false);
      setIsSubmitDisabled(true);
      return;
    }

    if (isMultiplexProcessing) {
      // Mutation finished; stay locked until redirect (isPending clears before navigation completes).
      setIsPrimaryButtonLoading(true);
      setIsSubmitDisabled(true);
      return;
    }

    setIsPrimaryButtonLoading(false);
    setIsSubmitDisabled(false);
  };

  const handleActiveUnitIndexChange = (activeUnitIndex: number | null) => {
    setIsSubmitDisabled(activeUnitIndex === null);
  };

  const handlePrimaryButtonClick = async () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS: {
        const isCommunityType = listing?.propertyType === COMMUNITY;

        const isRoomForRentCheckboxDisplayed = shouldDisplayRoomForRentCheckbox(
          isMultiplexType,
          isCommunityType,
          behavior,
        );

        setIsPrimaryButtonLoading(true);
        setIsSelectActionFinalStep(false);
        verifyInputAndGoToConfirmSteps(
          () => emitCreateListingEvent({ user }),
          // onValidationSuccess
          () => {
            // set isRoomForRent to false if checkbox was not displayed
            if (isRoomForRentCheckboxDisplayed === false) {
              setListing({
                type: 'isRoomForRent',
                payload: false,
              });
            }
          },
        );

        if (unitAlert === UNIT_ALERT.VALIDATED_WITHOUT_UNIT) {
          setUnitAlert(null);
        } else if (unitAlert === UNIT_ALERT.PROMPT_FOR_UNIT && listing.unit) {
          setUnitAlert(null);
        }

        break;
      }
      case STEPS.CONFIRM_ADDRESS: {
        setIsPrimaryButtonLoading(true);
        setIsSubmitDisabled(true);
        if (behavior === BEHAVIOR_OPTIONS.ADD) {
          try {
            if (isMultiplexType && user) {
              emitMultiplexConfirmAddressClickEvent({ user });
              // don't create the listing, go to unit entry step
              setStep(STEPS.UNIT_ENTRY);
              setIsPrimaryButtonLoading(false);
              setIsSubmitDisabled(false);
              return;
            }

            const result = await confirmProperty({
              isRoomForRent: listing.isRoomForRent,
              propertyType: listing.propertyType,
              fullAddress: listing.address,
              unit: listing.unit,
              numUnits: listing.numUnits,
              customerId: listing.customerId,
              duplicateAccountListingAlias: listing.duplicateAccountListingAlias,
            });

            if (isSelectActionFinalStep) {
              setStep(STEPS.SELECT_ACTION);
            } else {
              let route: ReturnType<typeof getListingRoute> | null = null;

              if (listing.duplicateAccountListingAlias) {
                route = getListingRoute({ listingAlias: listing.duplicateAccountListingAlias });
              } else if (result) {
                if (result.canAccessModularListing) {
                  setIsNewListing(true);
                  route = getCreateListingRoute({ listingAlias: result.responseData.listingAlias });
                } else {
                  route = getListingRoute({ listingAlias: result.responseData.listingAlias });
                }
              }

              if (route) {
                if (shouldRedirectToPayments) {
                  window.location.href = `${EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}/${result?.responseData.listingAlias}`;
                } else {
                  void router.push(route);
                }
                onClose();
              }
            }
          } catch (err) {
            const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
            const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';

            setErrorObj({ type: fieldWithError, payload: getErrorMessage(err) });
            handleErrorReset();
            return { path: null };
          }
        } else {
          editProperty();
        }

        break;
      }
      case STEPS.UNIT_ENTRY: {
        multiplexUnitEntryRef.current?.createMultiplex?.();
        break;
      }
      case STEPS.UNITS_ADDED: {
        const activeUnitListingAlias = multiplexUnitsAddedRef.current?.getActiveUnitListingAlias?.();

        const currentPropertyWithSelectedListingAlias: ZrmClickstreamListing = {
          ...listing,
          listingAlias: activeUnitListingAlias ?? undefined,
        };

        if (user) {
          emitMultiplexAddListingDetailsClickEvent({
            currentProperty: currentPropertyWithSelectedListingAlias,
            user,
          });
        }
        multiplexUnitsAddedRef.current?.goToSelectedUnitListingCreation?.();
        break;
      }
      default: {
        throw Error('unrecognized step: ' + step);
      }
    }
  };

  const handleSecondaryButtonClick = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        setIsSecondaryButtonLoading(true);
        setIsSelectActionFinalStep(true);
        verifyInputAndGoToConfirmSteps(() => emitSeeAllActionsEvent({ user }));
        break;
      case STEPS.CONFIRM_ADDRESS:
        emitGoBackAndEditAddressEvent({ user });
        setStep(STEPS.INPUT_ADDRESS);
        setIsRefocusingAddressInput(true);
        break;
      default:
        throw Error('unrecognized step: ' + step);
    }
  };

  const primaryButtonDataEventId = useMemo(() => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return ADD_PROPERTY_EVENT_IDS.CREATE_LISTING;

      case STEPS.UNIT_ENTRY:
        return MULTIPLEX_EVENT_IDS.MULTIPLEX_CONFIRM_UNIT_ENTRY_CLICK;

      case STEPS.UNITS_ADDED:
        return MULTIPLEX_EVENT_IDS.MULTIPLEX_ADD_LISTING_DETAILS_CLICK;

      default:
        if (isSelectActionFinalStep) {
          return ADD_PROPERTY_EVENT_IDS.CONFIRM_SEE_ALL_ACTIONS;
        }

        return ADD_PROPERTY_EVENT_IDS.CONFIRM_CREATE_LISTING;
    }
  }, [step, isSelectActionFinalStep]);

  const getPrimaryButtonTitle = () => {
    if (behavior === BEHAVIOR_OPTIONS.EDIT) {
      return 'Save changes';
    } else {
      const nonMultiplexButtonTitle = shouldRedirectToPayments ? 'Start collecting rent' : 'Confirm';

      const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : nonMultiplexButtonTitle;

      switch (step) {
        case STEPS.INPUT_ADDRESS:
          return 'Create listing';
        case STEPS.CONFIRM_ADDRESS:
          return confirmAddressButtonTitle;
        case STEPS.UNIT_ENTRY:
          return 'Save & continue';
        case STEPS.UNITS_ADDED:
          return 'Add listing details';
        default:
          return '';
      }
    }
  };

  const getSecondaryButtonTitle = () => {
    switch (step) {
      case STEPS.INPUT_ADDRESS:
        return 'See all available actions';
      case STEPS.CONFIRM_ADDRESS:
        return 'Edit property information';
      default:
        return '';
    }
  };

  const getSubheadingText = () => {
    if (shouldRedirectToPayments) {
      return 'Once you add your property, you can start collecting rent payments for free.';
    } else {
      return 'Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your perfect renter.';
    }
  };

  return (
    <Modal
      scrollDocument={false}
      useDismissProps={{ outsidePress: false }}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      data-eventid={ADD_PROPERTY_EVENT_IDS.OPEN}
    >
      <Modal.Header>
        <Heading level={1} textStyle="heading-xs">
          Add property
        </Heading>
      </Modal.Header>
      <Divider />
      <Modal.Body>
        <div aria-live="polite">
          {step === STEPS.INPUT_ADDRESS && (
            <Box
              css={{
                paddingBlock: { lg: '800' },
                paddingLeft: { lg: '800' },
                paddingRight: { lg: '20px' },
              }}
            >
              <Icon
                size="xl"
                css={{ color: 'icon.subtle', marginTop: { base: '600', md: 0 } }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ marginBlock: { base: '400', md: '600' } }}>
                First, let&rsquo;s add your property
              </Heading>

              <Paragraph
                css={{
                  paddingBottom: { mdDown: '200' },
                  marginBottom: {
                    md: unitAlert !== null || Boolean(propertyAlreadyPostedWarningMessage) ? '600' : '800',
                  },
                }}
              >
                {getSubheadingText()}
              </Paragraph>

              <AddPropertyForm
                listing={listing}
                contracts={contracts}
                errorObj={errorObj}
                isSubmitDisabled={isSubmitDisabled}
                setIsSubmitDisabled={setIsSubmitDisabled}
                behavior={behavior}
                unitAlert={unitAlert}
                propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
                onListingChange={handleListingChange}
                onCheckLocationRestriction={(keyName, newValue) =>
                  handleCheckLocationRestriction(onChangePropertyType, keyName, newValue)
                }
                isRefocusingAddressInput={isRefocusingAddressInput}
                setIsRefocusingAddressInput={setIsRefocusingAddressInput}
              />
            </Box>
          )}
          {step === STEPS.CONFIRM_ADDRESS && (
            <Box css={{ padding: { lg: '800' } }} data-testid="AddPropertyModal-ConfirmAddress">
              <Icon
                size="xl"
                css={{
                  color: 'icon.subtle',
                  marginTop: { base: '600', md: 0 },
                  marginBottom: '600',
                }}
                render={<IconHouseFilled />}
              />
              <Heading level={2} textStyle="heading-sm" css={{ paddingBottom: { base: '600', lg: '800' } }}>
                Is this information complete and correct?
              </Heading>

              <ConfirmAddress
                address={listing.address || ''}
                isRoomForRent={listing.isRoomForRent ?? false}
                numUnits={listing.numUnits}
                propertyType={listing.propertyType === PROPERTY_TYPES.CONDO ? 'Condo' : listing.propertyType || ''}
                unit={listing.unit}
                showImpreciseLocationAlert={isImpreciseLocation}
                user={user ?? undefined}
              />
            </Box>
          )}
          {step === STEPS.SELECT_ACTION && listing.listingAlias && (
            <SelectAvailableActions
              listingAlias={listing.listingAlias}
              triggerObject="posting_path"
              user={user ?? undefined}
            />
          )}
          {user?.features?.canDeleteListing &&
            user?.isPostingPathAdmin &&
            !propertyDetailsData?.isFeed &&
            listing.listingAlias &&
            behavior === BEHAVIOR_OPTIONS.EDIT && (
              <Box css={{ paddingInline: { base: '0', lg: '800' } }}>
                <DeleteButton listingAlias={listing.listingAlias} />
              </Box>
            )}
          {step === STEPS.UNIT_ENTRY && listing.address && (
            <MultiplexUnitEntry
              ref={multiplexUnitEntryRef}
              customerId={listing.customerId}
              fullAddress={listing.address}
              numUnits={listing.numUnits}
              onSuccess={handleMultiplexUnityEntrySuccess}
              onErrorChange={handleMultiplexUnityEntryErrorChange}
              onCreateStart={handleMultiplexUnityEntryCreateStart}
            />
          )}
          {step === STEPS.UNITS_ADDED && (
            <MultiplexUnitsAdded
              ref={multiplexUnitsAddedRef}
              hasPartialUnitsSucceeded={hasPartialUnitsSucceeded}
              unitsCreated={unitsCreated}
              onActiveUnitIndexChange={handleActiveUnitIndexChange}
            />
          )}
        </div>
      </Modal.Body>
      <Divider />
      {step !== STEPS.SELECT_ACTION && (
        <Modal.Footer css={{ justifyContent: step === STEPS.CONFIRM_ADDRESS ? 'space-between' : 'flex-end' }}>
          {step === STEPS.CONFIRM_ADDRESS && (
            <Box css={{ width: '40%' }}>
              <ListingActivationTerms actionText="By continuing" />
            </Box>
          )}

          <ButtonGroup aria-label="Action Buttons">
            {!shouldRedirectToPayments && !(step === STEPS.INPUT_ADDRESS && isMultiplexType) && (
              <TextButton
                tone="brand"
                onClick={handleSecondaryButtonClick}
                disabled={isSubmitDisabled}
                data-eventid={
                  step === STEPS.INPUT_ADDRESS
                    ? ADD_PROPERTY_EVENT_IDS.SEE_ALL_ACTIONS
                    : ADD_PROPERTY_EVENT_IDS.GO_BACK_AND_EDIT_ADDRESS
                }
              >
                {isSecondaryButtonLoading ? (
                  <Spinner size="sm" css={{ marginRight: '10px' }} />
                ) : (
                  getSecondaryButtonTitle()
                )}
              </TextButton>
            )}
            <Button
              data-testid="primary-button"
              data-eventid={primaryButtonDataEventId}
              fluid={false}
              tone="brand"
              emphasis="filled"
              onClick={handlePrimaryButtonClick}
              disabled={isSubmitDisabled}
              loading={isPrimaryButtonLoading}
            >
              {getPrimaryButtonTitle()}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AddPropertyModal;
